/**
 * Invoice Workbench contracts + API helpers (plan 03).
 *
 * Mirrors `app/schemas/ap_workbench.py` and the comparison-data payload built
 * by `app/services/ap/workbench_actions.py`. Row/status types come from
 * `@/lib/ap/invoices` — never fork them here.
 */

import { apiClient } from '@/lib/api-client'
import { formatDate } from '@/lib/ap/invoices'
import type { InvoiceStatus, ReviewCategory } from '@/lib/ap/invoices'
import type { ArCorrectionsPayload } from '@/lib/ap/argentina-draft'
import type { SapVendor } from '@/lib/ap/sap-vendor'

export { formatDate }

export type ActionName =
  | 'approve'
  | 'reject'
  | 'hold'
  | 'queue_qa'
  | 'return_to_review'
  | 'send_to_vendor'
  | 'reassign'
  | 'comment'
  | 'rematch'
  | 'edit'
  | 'push_to_sap'

// NOTICE: a finding worth a look (IVA category or INC rate consistency) that
// never blocks the invoice - deliberately excluded from BLOCKING_STATUSES on
// the backend (app/services/ap/matching/engine.py), so it never downgrades a
// MATCH, yet still shows in Resolution Required (see resolutionSteps below).
export type TraceStatus = 'PASS' | 'FAIL' | 'WARNING' | 'INFO' | 'NOTICE'

export interface TraceStep {
  step: string
  status: TraceStatus | string
  message?: string | null
  details?: Record<string, unknown> | null
}

export interface TracePolicyRef {
  policy_id: string | null
  policy_name: string | null
  rule_code: string | null
  policy_scope: string | null
  kind: 'policy' | 'automation_rule' | 'setting'
  href: string
}

/** One figure in the variance ledger. `op` drives the operator glyph, never colour alone. */
export interface ReconStep {
  key: string
  label: string
  op: 'base' | 'add' | 'subtract'
  amount: number
  running: number
}

/** A charge printed outside the billed lines, with the text it was read from. */
export interface ReconCharge {
  label: string | null
  amount: number | null
  page?: number | null
  printed_near?: string | null
  quote?: string | null
  appears_in_totals_block?: boolean
}

/** One base reconciled to the printed total. */
export interface ReconBase {
  base: number
  base_label: string
  steps: ReconStep[]
  reconciled_total: number
  printed_total: number
  residual: number
  residual_without_charges: number
  balances: boolean
}

/**
 * What the invoice states about its own gap. Never a guess: `detected` is false
 * whenever the invoice does not name a cause, including when the model named
 * something the backend could not find printed on the document.
 */
export interface ReconVerdict {
  detected: boolean
  statement?: string | null
  charges?: ReconCharge[] | null
  source?: string | null
  verified?: boolean
}

/** A line whose OCR-suggested IVA category implies a different rate than what
 *  the invoice actually applied - independent of the goods/services mapping
 *  the deterministic IVA check itself uses (app/services/ap/colombia_tax). */
export interface IvaCategoryMismatch {
  sku: string | null
  description: string
  predicted_category: 'transportation' | 'coffee_or_prepaid' | 'other'
  expected_rate_pct: number
  actual_rate_pct: number
  rate_source: 'line' | 'invoice_overall'
}

/** The one printed INC (impuesto al consumo) tax whose rate failed the 8%
 *  legal-rate check. */
export interface IncRateMismatch {
  label?: string | null
  rate?: number | null
  amount?: number | null
}

/** IVA and INC both printed on the same invoice - normally mutually exclusive
 *  categories (INC replaces IVA for the categories it applies to). */
export interface IvaIncConflict {
  iva_label: string
  iva_amount?: number | null
  inc_label: string
  inc_amount?: number | null
}

export interface Reconciliation {
  currency: string | null
  charges_captured: boolean
  inputs: Record<string, number | null>
  charges: ReconCharge[]
  bases: Record<string, ReconBase>
  closing_base: string | null
  balances: boolean
  residual: number | null
  verdict: ReconVerdict | null
  iva_category_mismatches?: IvaCategoryMismatch[]
  inc_rate_mismatch?: IncRateMismatch | null
  iva_inc_conflict?: IvaIncConflict | null
  // Whether the totals waterfall has anything to say: the invoice does not add
  // up, or it only adds up once a charge printed outside the billed lines is
  // accounted for (and the card's job is to name that charge). The ledger rides
  // on the Financial Integrity step whenever ANY of several reasons fired
  // (totals / IVA category / INC rate / IVA+INC both present), so this is what
  // tells the totals waterfall apart from a ledger attached only because one of
  // the other reasons fired on an otherwise-balanced invoice.
  totals_disagree?: boolean
  // A tax whose printed amount differs from the one the engine worked out. The
  // totals can balance perfectly while a rate is wrong, and that is worth
  // explaining too.
  taxes_disagree?: boolean
  /** The extra rows the reconciliation call describes, and its checked one-line notes on them. */
  items?: ReconItem[]
  item_notes?: ReconItemNote[]
}

/** An extra row of the tax panel - a tax the engine has no rule for, or a charge - by a stable id:
 *  `tax:<index in printed_taxes>` or `charge:<index in charges>`. */
export interface ReconItem {
  id: string
  group: 'tax' | 'charge'
  label: string | null
  amount: number | null
}

/** The reconciliation call's one-line description of an extra row, already checked server-side. */
export interface ReconItemNote {
  item_id: string
  category: 'vat' | 'withholding' | 'charge' | 'other'
  note: string
}

/** Where one extracted value was read from on the document (ingestion rule 11). */
export interface FieldSource {
  field: string
  line: string
  page?: number | null
}

export interface InvoiceDetail {
  id: number
  invoice_id: string
  vendor_name: string | null
  vendor_address: string | null
  buyer_name: string | null
  buyer_address: string | null
  shipping_address: string | null
  billing_address: string | null
  payment_terms: string | null
  other_header_fields: Record<string, unknown> | null
  invoice_metadata: Record<string, unknown> | null
  /** The printed line each extracted value was read from. Null on invoices
   *  ingested before the extractor recorded it. */
  field_sources: FieldSource[] | null
  /** Amounts printed outside the billed lines (a cargo charge, an airport tax). Null on invoices
   *  ingested before they were captured. */
  unbilled_charges: ReconCharge[] | null
  related_po_numbers: string[] | null
  related_grn_numbers: string[] | null
  invoice_date: string | null
  due_date: string | null
  subtotal: number | null
  tax: number | null
  grand_total: number | null
  currency: string | null
  /** CO or AR: which tax panel the Workbench shows (spec §9). */
  country: string
  line_items: Record<string, unknown>[] | null
  match_trace: TraceStep[] | null
  match_confidence: number | null
  ai_recommendation: unknown
  status: InvoiceStatus
  review_category: ReviewCategory | string | null
  notes: string | null
  gl_code: string | null
  assigned_to: string | null
  hold_until: string | null
  hold_reason: string | null
  sla_breached: boolean
  is_duplicate: boolean
  duplicate_of_id: number | null
  payment_batch_id: string | null
  paid_date: string | null
  source_filename: string | null
  has_file: boolean
  version: number
  created_at: string
  updated_at: string
  allowed_actions: ActionName[]
  /** Latest SAP posting (present once the invoice has been posted; also fetched via `getSapPosting`). */
  sap_posting?: SapPosting | null
}

// ─── SAP posting (mirrors the SAP contract: sap-preview / push-to-sap / sap-posting) ───────

export type SapStepKey = 'validate_format' | 'check_credentials' | 'check_connection' | 'post_document' | 'confirm'

export interface SapPostingStep {
  key: SapStepKey | string
  label: string
  status: 'done' | 'failed' | 'skipped'
  started_at: string | null
  finished_at: string | null
  detail: string | null
}

export interface SapMessage {
  severity: 'success' | 'info' | 'error'
  text: string
}

export interface SapPosting {
  id: number
  invoice_db_id: number
  invoice_id: string
  status: 'posted' | 'failed'
  sap_document_number: string | null
  company_code: string | null
  fiscal_year: string | null
  system_id: string | null
  client: string | null
  document_type: string | null
  payload: SapJournalEntryPayload | Record<string, unknown> | null
  response_xml: string | null
  messages: SapMessage[]
  steps: SapPostingStep[]
  posted_by: string | null
  posted_at: string | null
}

export interface SapTarget {
  system_id: string
  client: string
  company_code: string
  company_name: string
  document_type: string
  gl_account: string
  tax_code: string
}

export interface SapJournalEntryItem {
  GLAccount: string
  CurrencyCode: string
  AmountInTransactionCurrency: number | string
  Tax?: { TaxCode: string }
  DocumentItemText?: string
  PurchaseOrder?: string | null
  PurchaseOrderItem?: string | number | null
}

/** JournalEntryBulkCreateRequest-shaped JSON built from the invoice. Only the fields the UI reads are typed. */
export interface SapJournalEntryPayload {
  MessageHeader?: { CreationDateTime?: string }
  JournalEntryCreateRequest?: {
    JournalEntry?: {
      OriginalReferenceDocumentType?: string
      BusinessTransactionType?: string
      AccountingDocumentType?: string
      DocumentReferenceID?: string
      CompanyCode?: string
      DocumentDate?: string
      PostingDate?: string
      CreatedByUser?: string
      Item?: SapJournalEntryItem[]
      CreditorItem?: { Creditor?: string; AmountInTransactionCurrency?: number | string; DueCalculationBaseDate?: string }
    }
  }
}

export interface SapPreview {
  target: SapTarget
  payload: SapJournalEntryPayload
}

export interface PushToSapResult {
  invoice: InvoiceDetail
  posting: SapPosting
}

/** The five posting steps, in order. The dialog renders these as pending until the server's steps arrive;
 *  their labels come from useApLabels().sapStep. */
export const SAP_POSTING_STEP_KEYS: SapStepKey[] = ['validate_format', 'check_credentials', 'check_connection', 'post_document', 'confirm']

export interface NormalizedLine {
  description: string
  sku: string | null
  quantity: number | null
  unit: string | null
  unit_price: number | null
  total: number | null
  raw: Record<string, unknown>
  po_number?: string
  grn_number?: string
  line_key?: string
}

/** A PO line judged by its value, not unit price: a lump sum billed in parts, or split with other invoices. */
export interface ByValue {
  lines: number
  invoiced_amount: number | null
  received_value: number | null
  value_direction: string | null
  value_delta_pct: number | null
  shared: { other_invoices: string[]; combined_amount: number | null; combined_direction: string | null } | null
}

/** The prices the matcher compared, in the same unit (a trip's total against a PO rate per trip). */
export interface ComparedPrices {
  method: string | null
  basis: string | null
  invoice_price: number | null
  po_price: number | null
  price_delta_abs: number | null
  price_delta_pct: number | null
  tolerance_pct: number | null
  by_value?: ByValue | null
  /** What the matched PO line's receipts hold (null when the matcher recorded none). */
  received_qty?: number | null
}

export interface LineComparison {
  line_key: string
  invoice_line: NormalizedLine
  po_line: NormalizedLine | null
  grn_line: NormalizedLine | null
  po_number: string | null
  grn_number: string | null
  compared?: ComparedPrices | null
  /** Argentina: no PO lines to pair with, so the line is checked by the invoice's value (`value_check`). */
  checked_by?: 'value'
  /** The invoice's value-check status (app/services/ap/argentina/matching/value.py); null when not checked. */
  value_status?: string | null
}

/** Argentina: the invoice's net against the value of its goods receipts, as the last match stored it. */
export interface ValueCheck {
  status: string | null
  invoice_net: number | null
  receipts_value: number | null
  currency: string | null
  receipt_count: number
  delta_abs: number | null
  delta_pct: number | null
  discount_pct: number | null
}

export interface RelatedPo {
  id: number
  po_number: string
  vendor_name: string | null
  buyer_name: string | null
  order_date: string | null
  line_items: NormalizedLine[]
  po_grand_total: number | null
  grand_total: number | null
  status: string
  has_file: boolean
}

export interface RelatedGrn {
  id: number
  grn_number: string
  po_number: string | null
  received_date: string | null
  line_items: NormalizedLine[]
  status: string
  has_file: boolean
}

export interface DocumentRef {
  id: number
  has_file: boolean
  file_url: string | null
  po_number?: string
  grn_number?: string
}

export interface Suggestion {
  heuristic_id: number
  exception_type: string
  message: string
  action: string
  confidence: number
}

export interface ComparisonData {
  invoice_id: string
  invoice_db_id: number
  version: number
  invoice_header_data: Record<string, unknown>
  vendor_name: string | null
  grand_total: number | null
  line_item_comparisons: LineComparison[]
  related_pos: RelatedPo[]
  related_grns: RelatedGrn[]
  missing_po_numbers: string[]
  missing_grn_numbers: string[]
  invoice_notes: string | null
  invoice_status: InvoiceStatus
  review_category: string | null
  match_trace: TraceStep[]
  match_facts: unknown
  match_confidence: number | null
  trace_policy_index: Record<string, TracePolicyRef>
  gl_code: string | null
  related_documents: { invoice: DocumentRef; pos: DocumentRef[]; grns: DocumentRef[] }
  suggestion: Suggestion | null
  allowed_actions: ActionName[]
  /** Argentina only (absent for Colombia): null until a match has checked the invoice's value. */
  value_check?: ValueCheck | null
  /** Argentina only (absent for Colombia): the SAP vendor number the match found, or why there is none. */
  sap_vendor?: SapVendor
}

export interface Siblings {
  ids: number[]
  index: number
  total: number
  queue_label: string
  prev_id: number | null
  next_id: number | null
}

export interface TimelineEntry {
  id: number
  timestamp: string
  user: string
  action: string
  summary: string
  details: Record<string, unknown> | null
  phase: 'ingestion' | 'extraction' | 'linking' | 'matching' | 'policy' | 'human' | 'payment'
}

export interface InvoiceComment {
  id: number
  invoice_id: number
  user: string
  text: string
  type: string
  created_at: string
}

export interface ActionResult {
  invoice_id: number
  invoice_id_str: string
  from_status: InvoiceStatus
  to_status: InvoiceStatus
  audit_id: number | null
  message: string
  allowed_actions: ActionName[]
  version: number
}

export type EmailKind =
  | 'price'
  | 'quantity'
  | 'variance'
  | 'generic'
  | 'iva_category'
  | 'inc_rate'
  | 'iva_inc_exclusivity'
  // Rows of the tax panel - taxes, withholdings, charges - the reviewer ticked to ask about.
  | 'tax_query'

/** One row of the tax panel ticked for a vendor query (`TaxQueryItem` in app/schemas/ap_workbench.py). */
export interface TaxQueryItem {
  group: 'tax' | 'withholding' | 'charge'
  label: string
}

export interface EmailDraft {
  to_email: string | null
  subject: string
  body: string
  generated_by: 'ai' | 'template'
}

export interface TeachResponse {
  draft_policy: Record<string, unknown>
  analysis: {
    conflicts: unknown[]
    overrides: unknown[]
    clarifications: string[]
    suggested_instructions: string[]
    refined_instruction?: string | null
    warnings: string[]
    is_valid: boolean
  }
  policy_id: string | null
}

/** Header keys the backend allow-lists (S13). Anything else is rejected with 422. */
export const HEADER_EDITABLE_FIELDS = [
  'invoice_id',
  'vendor_name',
  'invoice_date',
  'due_date',
  'subtotal',
  'tax',
  'grand_total',
  'currency',
  'payment_terms',
  'vendor_address',
  'buyer_name',
  'buyer_address',
  'billing_address',
  'shipping_address',
  'notes',
] as const

export type HeaderField = (typeof HEADER_EDITABLE_FIELDS)[number]
export type HeaderDraft = Partial<Record<HeaderField, string | number | null>>

// ─── API ────────────────────────────────────────────────────────────────────

const base = (id: number) => `/api/invoices/${id}`

export const workbenchApi = {
  detail: (id: number) => apiClient.get<InvoiceDetail>(base(id)),
  comparison: (id: number) => apiClient.get<ComparisonData>(`${base(id)}/comparison-data`),
  siblings: (id: number, queue: string | null) =>
    apiClient.get<Siblings>(`${base(id)}/siblings${queue ? `?queue=${encodeURIComponent(queue)}` : ''}`),
  auditLog: (id: number) => apiClient.get<TimelineEntry[]>(`${base(id)}/audit-log`),
  comments: (id: number) => apiClient.get<InvoiceComment[]>(`${base(id)}/comments`),
  addComment: (id: number, text: string) => apiClient.post<InvoiceComment>(`${base(id)}/comments`, { text }),
  approve: (id: number, body: { version: number; reason?: string; two_way_override?: boolean }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/approve`, body),
  reject: (id: number, body: { version: number; reason: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/reject`, body),
  hold: (id: number, body: { version: number; hold_days: number; reason: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/hold`, body),
  queue: (id: number, body: { version: number; target: 'qa_approval' | 'needs_review'; reason?: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/queue`, body),
  sendToVendor: (id: number, body: { version: number; to_email?: string; subject?: string; message: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/send-to-vendor`, body),
  reassign: (
    id: number,
    body: { version: number; mode: 'assign' | 'review'; assignee?: string; message?: string; note?: string }
  ) => apiClient.post<ActionResult>(`${base(id)}/actions/reassign`, body),
  rematch: (id: number, version: number) => apiClient.post<ActionResult>(`${base(id)}/actions/rematch`, { version }),
  /** Header fields and tax-input corrections in one call: one transaction, one version bump,
   *  one audit row, one engine re-run. `confirmed` is required when the change moves a tax. */
  updateHeader: (
    id: number,
    body: HeaderDraft & { version: number; tax_inputs?: Record<string, string | null>; ar_corrections?: ArCorrectionsPayload; confirmed?: boolean; note?: string }
  ) => apiClient.put<InvoiceDetail>(`${base(id)}/header`, body),
  updateLinks: (id: number, body: { version: number; related_po_numbers?: string[]; related_grn_numbers?: string[] }) =>
    apiClient.put<ActionResult>(`${base(id)}/links`, body),
  /** `items` carries the rows a reviewer ticked for a 'tax_query', so the draft names those and no
   *  others; the server reads their figures off the stored determination and the invoice's charges. */
  emailDraft: (id: number, kind: EmailKind, items?: TaxQueryItem[]) =>
    apiClient.post<EmailDraft>(`${base(id)}/email-draft`, { kind, items: items ?? [] }),
  teach: (id: number, body: { instruction: string; trace_step?: string; confirm: boolean }) =>
    apiClient.post<TeachResponse>(`${base(id)}/teach`, body),
  sapPreview: (id: number) => getSapPreview(id),
  pushToSap: (id: number, comment?: string | null, version?: number) => pushToSap(id, comment, version),
  sapPosting: (id: number) => getSapPosting(id),
}

/** GET /api/invoices/{id}/sap-preview — target + JournalEntryBulkCreateRequest payload built from the invoice. */
export function getSapPreview(id: number): Promise<SapPreview> {
  return apiClient.get<SapPreview>(`${base(id)}/sap-preview`)
}

/** POST /api/invoices/{id}/actions/push-to-sap — performs the posting; the invoice lands in `posted`. */
export function pushToSap(id: number, comment?: string | null, version?: number): Promise<PushToSapResult> {
  return apiClient.post<PushToSapResult>(`${base(id)}/actions/push-to-sap`, { comment: comment ?? null, ...(version != null ? { version } : {}) })
}

/** GET /api/invoices/{id}/sap-posting — latest posting, or 404 (rejected promise) when none exists. */
export function getSapPosting(id: number): Promise<SapPosting> {
  return apiClient.get<SapPosting>(`${base(id)}/sap-posting`)
}

/** The one PDF endpoint (R4 / plan 08 E5). Fetched raw with a bearer token, never via apiClient. */
export function documentFileUrl(kind: 'invoice' | 'purchase_order' | 'goods_receipt_note', id: number): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return `${apiUrl}${basePath}/api/ap/documents/${kind}/${id}/file`
}

/** Workbench URL that keeps the originating queue (R14). */
export function workbenchUrl(invoiceId: number, queue: string | null): string {
  return `/workbench?invoice=${invoiceId}${queue ? `&queue=${encodeURIComponent(queue)}` : ''}`
}

// ─── Trace helpers (ported semantics from legacy MatchTrace / LineItemComparisonTable) ─────

/** Strip embedded JSON blobs and map raw literals to prose (legacy MatchTrace.tsx:5-27). */
export function cleanTraceMessage(message: string | null | undefined): string {
  if (!message) return ''
  let out = message
  out = out.replace(/\{[^{}]*\}/g, (m) => (m.length > 40 ? '' : m))
  out = out.replace(/\bnull\b/gi, 'not available').replace(/\btrue\b/g, 'yes').replace(/\bfalse\b/g, 'no')
  out = out.replace(/\s{2,}/g, ' ').trim()
  return out
}

export { classifyLine, valueCheckClass, type LineClass } from './line-class'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** A number from an untyped payload. Accepts strings: the Colombia tax payload
 *  ships Decimal money that way, and a ledger that silently blanks out is worse
 *  than one that reads a string. */
function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const out = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(out) ? out : null
}

function toStep(value: unknown): ReconStep | null {
  if (!isRecord(value)) return null
  const amount = num(value.amount)
  const running = num(value.running)
  if (typeof value.key !== 'string' || typeof value.label !== 'string') return null
  if (amount === null || running === null) return null
  const op = value.op === 'add' || value.op === 'subtract' ? value.op : 'base'
  return { key: value.key, label: value.label, op, amount, running }
}

function toCharge(value: unknown): ReconCharge | null {
  if (!isRecord(value)) return null
  return {
    label: typeof value.label === 'string' ? value.label : null,
    amount: num(value.amount),
    page: num(value.page),
    printed_near: typeof value.printed_near === 'string' ? value.printed_near : null,
    quote: typeof value.quote === 'string' ? value.quote : null,
    appears_in_totals_block: value.appears_in_totals_block === true,
  }
}

function toIvaCategoryMismatch(value: unknown): IvaCategoryMismatch | null {
  if (!isRecord(value)) return null
  const expected = num(value.expected_rate_pct)
  const actual = num(value.actual_rate_pct)
  const category = value.predicted_category
  if (expected === null || actual === null
      || (category !== 'transportation' && category !== 'coffee_or_prepaid' && category !== 'other')) {
    return null
  }
  return {
    sku: typeof value.sku === 'string' ? value.sku : null,
    description: typeof value.description === 'string' ? value.description : '',
    predicted_category: category,
    expected_rate_pct: expected,
    actual_rate_pct: actual,
    rate_source: value.rate_source === 'invoice_overall' ? 'invoice_overall' : 'line',
  }
}

function toIncRateMismatch(value: unknown): IncRateMismatch | null {
  if (!isRecord(value)) return null
  return {
    label: typeof value.label === 'string' ? value.label : null,
    rate: num(value.rate),
    amount: num(value.amount),
  }
}

function toIvaIncConflict(value: unknown): IvaIncConflict | null {
  if (!isRecord(value)) return null
  if (typeof value.iva_label !== 'string' || typeof value.inc_label !== 'string') return null
  return {
    iva_label: value.iva_label,
    iva_amount: num(value.iva_amount),
    inc_label: value.inc_label,
    inc_amount: num(value.inc_amount),
  }
}

function toBase(value: unknown): ReconBase | null {
  if (!isRecord(value)) return null
  const steps = Array.isArray(value.steps) ? value.steps.map(toStep).filter((s): s is ReconStep => s !== null) : []
  const base = num(value.base)
  const reconciled = num(value.reconciled_total)
  const printed = num(value.printed_total)
  const residual = num(value.residual)
  if (steps.length === 0 || base === null || reconciled === null || printed === null || residual === null) return null
  return {
    base,
    base_label: typeof value.base_label === 'string' ? value.base_label : 'Base',
    steps,
    reconciled_total: reconciled,
    printed_total: printed,
    residual,
    residual_without_charges: num(value.residual_without_charges) ?? 0,
    balances: value.balances === true,
  }
}

/**
 * The reconciliation ledger a trace step carries, or null.
 *
 * `details` is untyped, so this is a runtime guard rather than a cast, and it is
 * total: every invoice ingested before this feature returns null and the
 * Workbench renders exactly as it did before.
 */
export function reconciliationOf(step: TraceStep): Reconciliation | null {
  const raw = step.details?.reconciliation
  if (!isRecord(raw)) return null
  const bases: Record<string, ReconBase> = {}
  if (isRecord(raw.bases)) {
    for (const [key, value] of Object.entries(raw.bases)) {
      const built = toBase(value)
      if (built) bases[key] = built
    }
  }
  // A half-rendered ledger is worse than none: an operator would read a partial
  // waterfall as the whole argument.
  if (Object.keys(bases).length === 0) return null
  const verdict = isRecord(raw.verdict) && typeof raw.verdict.detected === 'boolean'
    ? {
        detected: raw.verdict.detected,
        statement: typeof raw.verdict.statement === 'string' ? raw.verdict.statement : null,
        charges: Array.isArray(raw.verdict.charges)
          ? raw.verdict.charges.map(toCharge).filter((c): c is ReconCharge => c !== null)
          : [],
        source: typeof raw.verdict.source === 'string' ? raw.verdict.source : null,
        verified: raw.verdict.verified === true,
      }
    : null
  return {
    currency: typeof raw.currency === 'string' ? raw.currency : null,
    charges_captured: raw.charges_captured === true,
    inputs: isRecord(raw.inputs)
      ? Object.fromEntries(Object.entries(raw.inputs).map(([k, v]) => [k, num(v)]))
      : {},
    charges: Array.isArray(raw.charges)
      ? raw.charges.map(toCharge).filter((c): c is ReconCharge => c !== null)
      : [],
    bases,
    closing_base: typeof raw.closing_base === 'string' ? raw.closing_base : null,
    balances: raw.balances === true,
    residual: num(raw.residual),
    verdict,
    iva_category_mismatches: Array.isArray(raw.iva_category_mismatches)
      ? raw.iva_category_mismatches.map(toIvaCategoryMismatch).filter((m): m is IvaCategoryMismatch => m !== null)
      : [],
    inc_rate_mismatch: toIncRateMismatch(raw.inc_rate_mismatch),
    iva_inc_conflict: toIvaIncConflict(raw.iva_inc_conflict),
    totals_disagree: raw.totals_disagree === true,
    taxes_disagree: raw.taxes_disagree === true,
    ...itemsOf(raw),
  }
}

const NOTE_CATEGORIES = ['vat', 'withholding', 'charge', 'other'] as const

/** The extra rows and the call's notes on them, off a raw ledger. Parsed on their own so they reach the
 *  tax panel even when the ledger has no base to draw a waterfall from. */
function itemsOf(raw: Record<string, unknown>): { items: ReconItem[]; item_notes: ReconItemNote[] } {
  const items: ReconItem[] = Array.isArray(raw.items)
    ? raw.items.filter(isRecord).flatMap((item) =>
        typeof item.id === 'string' && (item.group === 'tax' || item.group === 'charge')
          ? [{ id: item.id, group: item.group, label: typeof item.label === 'string' ? item.label : null, amount: num(item.amount) }]
          : [])
    : []
  const verdict = isRecord(raw.verdict) ? raw.verdict : {}
  const item_notes: ReconItemNote[] = Array.isArray(verdict.item_notes)
    ? verdict.item_notes.filter(isRecord).flatMap((note) =>
        typeof note.item_id === 'string' && typeof note.note === 'string'
          ? [{
              item_id: note.item_id,
              category: (NOTE_CATEGORIES as readonly unknown[]).includes(note.category)
                ? (note.category as ReconItemNote['category'])
                : 'other',
              note: note.note,
            }]
          : [])
    : []
  return { items, item_notes }
}

/** The reconciliation call's one-line notes, keyed by row id (`tax:<i>` / `charge:<i>`), off whichever
 *  trace step carries the ledger. Empty on invoices matched before the call ran on every invoice. */
export function itemNotesOf(trace: TraceStep[]): Record<string, ReconItemNote> {
  for (const step of trace) {
    const raw = step.details?.reconciliation
    if (!isRecord(raw)) continue
    return Object.fromEntries(itemsOf(raw).item_notes.map((note) => [note.item_id, note]))
  }
  return {}
}

/**
 * The first trace step carrying a reconciliation ledger with a real mismatch to show.
 *
 * A ledger rides on the Financial Integrity step whenever ANY of several reasons fired
 * (totals / IVA category / INC rate / IVA+INC both present) - that is what lets those
 * findings reach the reconciliation call at all. But the waterfall this builds answers
 * two questions only: does this document add up, and does a tax on it come to what we
 * make it. When neither is in question the card has nothing to say, and saying it
 * anyway reads as a mismatch that isn't real - an operator looking at a DIAN
 * declaration whose figures agreed perfectly was shown a column of zeros ending in
 * "nothing unexplained".
 *
 * Gated here AND in the card itself (VarianceReconciliation returns null on a ledger
 * that closes with nothing to name). Belt and braces on purpose: ledgers stored on
 * invoices ingested before this check exists still carry the old flag, and they must
 * stop rendering without anyone having to re-match them.
 */
export function reconciliationStep(trace: TraceStep[]): { step: TraceStep; reconciliation: Reconciliation } | null {
  for (const step of trace) {
    const reconciliation = reconciliationOf(step)
    if (reconciliation && (reconciliation.totals_disagree || reconciliation.taxes_disagree)) {
      return { step, reconciliation }
    }
  }
  return null
}

/**
 * The vendor-query draft that fits a failing step.
 *
 * Picking a template, not deciding anything: the drafts differ in which figures
 * they carry, so a quantity dispute does not go out quoting a totals variance.
 */
export function emailKindForStep(step: TraceStep): EmailKind {
  if (step.details?.iva_category_mismatches) return 'iva_category'
  if (step.details?.inc_rate_mismatch) return 'inc_rate'
  if (step.details?.iva_inc_conflict) return 'iva_inc_exclusivity'
  if (step.details?.reconciliation) return 'variance'
  const name = step.step.toLowerCase()
  if (name.includes('price')) return 'price'
  if (name.includes('quantit')) return 'quantity'
  return 'generic'
}

export function resolutionSteps(trace: TraceStep[]): TraceStep[] {
  return trace.filter((s) => {
    // NOTICE never blocks the invoice, but it still belongs here: the same
    // Create Policy / Email Vendor / Hold actions every blocking exception
    // gets (see ResolutionRequired in MatchDetails.tsx).
    if (s.status !== 'FAIL' && s.status !== 'NOTICE') return false
    if (s.details && typeof s.details.line_key === 'string') return false
    const n = s.step.toLowerCase()
    if (n.startsWith('item ')) return false
    if (n.includes('final result')) return false
    if ((n.includes('price') || n.includes('quantity')) && n.includes('match')) return false
    return true
  })
}

export function summaryCounts(trace: TraceStep[]) {
  return trace.reduce(
    (acc, s) => {
      if (s.status === 'PASS') acc.passed += 1
      else if (s.status === 'FAIL') acc.failed += 1
      else if (s.status === 'WARNING') acc.warnings += 1
      else acc.info += 1
      return acc
    },
    { passed: 0, failed: 0, warnings: 0, info: 0 }
  )
}

export function formatMoney(value: number | null | undefined, currency?: string | null): string {
  if (value == null || Number.isNaN(value)) return '—'
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(value)
  } catch {
    return value.toFixed(2)
  }
}

/**
 * Ledger money: always two decimals, so a right-aligned column lines up on the
 * decimal point. `formatMoney` sets no minimum and Intl's default for COP is 0,
 * which staggers whole-number rows against fractional ones in the same column.
 */
export function formatLedgerMoney(value: number | null | undefined, currency?: string | null): string {
  if (value == null || Number.isNaN(value)) return '\u2014'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value)
      // Intl emits an ASCII hyphen; the ledger signs its subtractions with a
      // real minus, and one column must not mix the two.
      .replace('-', '−')
  } catch {
    return value.toFixed(2)
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export const PHASE_COLORS: Record<TimelineEntry['phase'], string> = {
  ingestion: 'bg-blue-100 text-blue-700',
  extraction: 'bg-indigo-100 text-indigo-700',
  linking: 'bg-cyan-100 text-cyan-700',
  matching: 'bg-violet-100 text-violet-700',
  policy: 'bg-purple-100 text-purple-700',
  human: 'bg-orange-100 text-orange-700',
  payment: 'bg-teal-100 text-teal-700',
}
